import {AxiosHeaders, AxiosRequestConfig, AxiosResponse} from "axios";
import {ApiService} from "../constants";

const TTL = 30 * 1000;
const BypassedUrls = [
    ApiService.beelajar.getEventDetail,
    ApiService.beelajar.getEvent,
    ApiService.beelajar.getEventList,
]

const DB_NAME = "axios-cache-db";
const STORE_NAME = "cache";
const DB_VERSION = 1;

const CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes
const META_STORE = "meta";
const LAST_CLEANUP_KEY = "last-cleanup";

type PersistentCacheEntry<T = any> = {
    key: string;
    data: T;
    expiresAt: number;
};

export type CachedAxiosResponse<T = any> = {
    data: T;
    status: number;
    statusText: string;
    headers: Record<string, any>;
};

export const serializeAxiosResponse = <T>(
    response: AxiosResponse<T>
): CachedAxiosResponse<T> => ({
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers:
        response.headers instanceof AxiosHeaders
            ? response.headers.toJSON()
            : response.headers,
});

export const deserializeAxiosResponse = <T>(
    cached: CachedAxiosResponse<T>
): AxiosResponse<T> => ({
    data: cached.data,
    status: cached.status,
    statusText: cached.statusText,
    headers: new AxiosHeaders(cached.headers),
    config: {} as any,
    request: null as any,
});

const openDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;

            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: "key" });
                store.createIndex("expiresAt", "expiresAt");
            }

            if (!db.objectStoreNames.contains(META_STORE)) {
                db.createObjectStore(META_STORE, { keyPath: "key" });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const cleanupExpiredEntries = async (): Promise<void> => {
    const db = await openDB();
    const now = Date.now();

    return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);

        const cursorReq = store.openCursor();

        cursorReq.onsuccess = () => {
            const cursor = cursorReq.result;
            if (!cursor) {
                resolve();
                return;
            }

            if (cursor.value.expiresAt <= now) {
                cursor.delete();
            }

            cursor.continue();
        };
    });
};


// Whose data a response is. null means "can't tell", so the request must not be cached.
export const getCacheScope = (config: AxiosRequestConfig): string | null => {
    const headers = (config.headers ?? {}) as unknown as Record<string, unknown>;

    const userId = headers['Binusian-Id'];
    if (userId) {
        return `user:${String(userId)}`;
    }

    const authorization = headers['Authorization'];
    if (!authorization || String(authorization).startsWith('Basic ')) {
        return 'anonymous';
    }

    // Bearer token with no known user (e.g. Microsoft Graph "me" calls)
    return null;
};

export const createCacheKey = (url: string, config: AxiosRequestConfig) => {
    return `axios-cache:${JSON.stringify({
        scope: getCacheScope(config),
        url,
        method: config.method || 'get',
        params: config.params,
        data: config.data,
    })}`;
};

export const getCache = async <T>(key: string): Promise<T | null> => {
    await hardReloadClear;
    const db = await openDB();

    return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
            const entry = request.result as PersistentCacheEntry<T> | undefined;

            if (!entry) {
                resolve(null);
                return;
            }

            if (Date.now() > entry.expiresAt) {
                store.delete(key);
                resolve(null);
                return;
            }

            resolve(entry.data);
        };

        request.onerror = () => resolve(null);
    });
};

export const setCache = async <T>(key: string, data: T): Promise<void> => {
    const db = await openDB();
    const now = Date.now();

    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).put({
            key,
            data,
            expiresAt: now + TTL,
        });

        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });

    const lastCleanup = await new Promise<number>((resolve) => {
        const tx = db.transaction(META_STORE, "readonly");
        const req = tx.objectStore(META_STORE).get(LAST_CLEANUP_KEY);

        req.onsuccess = () => resolve(req.result?.value ?? 0);
        req.onerror = () => resolve(0);
    });

    if (now - lastCleanup > CLEANUP_INTERVAL) {
        await cleanupExpiredEntries();

        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(META_STORE, "readwrite");
            tx.objectStore(META_STORE).put({
                key: LAST_CLEANUP_KEY,
                value: now,
            });

            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
};


export const clearCache = async (): Promise<void> => {
    const db = await openDB();

    return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_NAME, META_STORE], "readwrite");

        tx.objectStore(STORE_NAME).clear();
        tx.objectStore(META_STORE).clear();

        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
};

// The browser exposes no "hard reload" signal to the page, so we watch for the shortcut keys instead.
// The keydown leaves a short-lived flag in sessionStorage; the next page load clears the cache only
// if that flag is fresh and the load really was a reload. Reloads not started from the keyboard
// (reload button, DevTools menu) are not detected.
const HARD_RELOAD_FLAG = "hard-reload-requested";
const HARD_RELOAD_FLAG_TTL = 10 * 1000;

const isHardReloadShortcut = (event: KeyboardEvent): boolean => {
    const key = event.key?.toLowerCase();
    const hasModifier = event.ctrlKey || event.metaKey;

    return (hasModifier && event.shiftKey && key === "r")      // Ctrl/Cmd+Shift+R
        || ((hasModifier || event.shiftKey) && key === "f5");  // Ctrl+F5, Shift+F5
};

const flagHardReload = () => {
    try {
        sessionStorage.setItem(HARD_RELOAD_FLAG, Date.now().toString());
    } catch (e) {
        console.error(e);
    }
};

// Always removes the flag so a stale one can't trigger a later load.
const consumeHardReloadFlag = (): boolean => {
    try {
        const requestedAt = Number(sessionStorage.getItem(HARD_RELOAD_FLAG));
        sessionStorage.removeItem(HARD_RELOAD_FLAG);
        return requestedAt > 0 && Date.now() - requestedAt < HARD_RELOAD_FLAG_TTL;
    } catch {
        return false;
    }
};

const isReloadNavigation = (): boolean => {
    try {
        const [navigation] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
        return navigation?.type === "reload";
    } catch {
        return false;
    }
};

const isHardReload = (): boolean => {
    const flagged = consumeHardReloadFlag();
    return flagged && isReloadNavigation();
};

window.addEventListener("keydown", (event) => {
    if (isHardReloadShortcut(event)) {
        flagHardReload();
    }
}, true);

// Starts at module load; reads wait on it so they can't return entries a hard reload was meant to drop.
const hardReloadClear: Promise<void> = isHardReload()
    ? clearCache().catch((e) => console.error(e))
    : Promise.resolve();

export const checkBypassedCache = (toBeCheckedUrl: string): boolean => {
    for (const url of BypassedUrls) {
        if (toBeCheckedUrl.includes(url))
            return true;
    }
    return false;
}